import { css, styled } from "styled-components";
import { Form, FormProps, Input, Typography } from "antd";
import CustomButton from "@/components/custom-button/custom-button";
import { adjustColorOpacity } from "@/utils/rgba-hexadecimal/rgba-hexadecimal";

const ContentPostStyled = styled.div<{ $disabledMission : boolean}>`
  display: flex;
  padding: 16px 24px; 
  flex-direction: column;
  align-items: center;
  gap: 32px;
  align-self: stretch;
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.theme?.contentBgOpaque};

  ${({ $disabledMission }) =>{
    return  $disabledMission && css`
      filter: grayscale(100%);
      opacity: 0.7;
      `
  }}
`;

const DescriptionStyled = styled(Typography)`
  color: ${({ theme }) => theme?.colors?.text?.headings};
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: "Red Hat Display", Arial;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 150%; /* 24px */
  display: flex;
  align-items: flex-start;
  gap: 5px;
  span.number, span.rouse-color {
    color: ${({ theme }) => theme?.colors?.theme?.brandColor3};
  }
`;

const ContentTextCopy = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const ContentErrorStyled = styled.div`
  color: ${({ theme }) => theme?.colors?.theme?.warning};
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ContentErrorSecondaryStyled = styled.div`
  color: ${({ theme }) => theme?.colors?.theme?.error};
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
`;

const ContentInputStyled = styled(Input)`
  &.ant-input.ant-input-outlined {
    border-radius: ${({ theme }) => theme?.sizes?.borderRadius?.input};
    border: 1px solid ${({ theme }) => adjustColorOpacity(theme?.colors?.text?.neutral, 0.5)};
    box-shadow: none;
    color: ${({ theme }) => theme?.colors?.text?.neutral};
    
    &::placeholder {
      color: ${({ theme }) => theme?.colors?.text?.neutral};
    }
  }

  &.ant-input.ant-input-outlined:focus {
    border: 1px solid ${({ theme }) => adjustColorOpacity(theme?.colors?.text?.neutral, 0.5)};
    box-shadow: none;
  }

  &.ant-input.ant-input-outlined.ant-input-status-error {
    border-color: ${({ theme }) => theme?.colors?.theme?.error};
  }
`;

const ContentHashtagStyled = styled.div`
  display: flex;
  width: 100%;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  min-height: 80px;

  border-radius: 12px;
  background: ${({ theme }) => theme?.colors?.text?.muted};

  span.hashtag {
    color: ${({ theme }) => theme?.colors?.theme?.headings};
  }
  span.idExternal {
    margin-left: 4px;
    color: ${({ theme }) => theme?.colors?.text?.headings};
  }
  span.copy-text {
    color: ${({ theme }) => theme?.colors?.common?.green};
  }
  
  span {
    transition: all 0.3s ease-in-out;
    opacity: 1;
    transform: translateY(0);
  }
  
  span {
    &.fade-enter {
      opacity: 0;
      transform: translateY(-10px);
    }
    &.fade-exit {
      opacity: 0;
      transform: translateY(10px);
    }
  }
`;

const ButtonCopyStyled = styled(CustomButton)`
  border: none !important;
  box-shadow: none !important;
  padding: 0px !important;
  height: auto !important;
  min-height: auto !important;
  background-color: transparent !important;
  &:hover {
    background-color: transparent;
  }
  span {
    color: ${({ theme }) => theme?.colors?.text?.headings};
  }
`;

const NoteStyled = styled.div`
  color: ${({ theme }) => theme?.colors?.text?.headings};
  display: flex;
  justify-content: center;
  flex-wrap: no-wrap;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
  padding: 0px 10px;
  span.note-text {
    font-weight: 700;
  }
  a, .note-text {
    color: ${({ theme }) => theme?.colors?.theme?.brandColor3};
  }
  a {
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }
`;

const FormStyled = styled(Form)<FormProps>`
  width: 100%;
  padding: 0px;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 32px;

  .ant-form-item-label{
    label {
      color: ${({ theme }) => theme?.colors?.text?.headings};
    }
  }
`;

export const BorderlessButton = styled(CustomButton)`
  &.ant-btn {
    border: none !important;
    box-shadow: none !important;
    padding: 10px 24px !important;
    height: 40px !important;
    border-radius: 40px !important;

    &:hover, &:focus {
      border: none !important;
      box-shadow: none !important;
    }

    &:disabled {
      border: none !important;
      box-shadow: none !important;
    }
  }
`;

const ContentButtonStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContentThanksAndViewPostStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  text-align: center;

  .thanks-text {
    color: ${({ theme }) => theme?.colors?.theme?.success};
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%; /* 24px */
  }
  .view-post-text {
    color: ${({ theme }) => theme?.colors?.theme?.info};
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%; /* 21px */
  }

  .search-text {
    width: 100%;
    max-width: 356px;
    margin: 0 auto;
    color: ${({ theme }) => theme?.colors?.theme?.brandColor3};
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 120%; /* 24px */
  }
`;

export {
  ContentPostStyled,
  DescriptionStyled,
  ContentTextCopy,
  NoteStyled,
  ContentHashtagStyled,
  ButtonCopyStyled,
  FormStyled,
  ContentErrorStyled,
  ContentButtonStyled,
  ContentInputStyled,
  ContentThanksAndViewPostStyled,
  ContentErrorSecondaryStyled
};

